import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ReviewCard } from './ReviewCard';
import type { Review } from '../../types/content';
interface ReviewCarouselProps {
  reviews: Review[];
}
export function ReviewCarousel({
  reviews
}: ReviewCarouselProps) {
  const reduce = useReducedMotion();
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const sync = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const items = Array.from(el.children) as HTMLElement[];
    const stride = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : el.clientWidth;
    setActive(Math.min(items.length - 1, Math.round(el.scrollLeft / Math.max(stride, 1))));
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    setOverflowing(el.scrollWidth > el.clientWidth + 4);
  }, []);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [sync]);
  const goTo = (index: number) => {
    const el = trackRef.current;
    if (!el) return;
    const items = Array.from(el.children) as HTMLElement[];
    const target = items[Math.max(0, Math.min(index, items.length - 1))];
    el.scrollTo({
      left: target.offsetLeft - items[0].offsetLeft,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    }
  };
  const arrowClass = 'inline-flex h-10 w-10 items-center justify-center rounded-full border border-champagne-100/20 text-champagne-100 transition-colors duration-150 ease-out hover:border-champagne-300/60 hover:text-champagne-50 disabled:cursor-not-allowed disabled:opacity-30';
  return <div role="region" aria-roledescription="carousel" aria-label="Client reviews" data-id="element-288" data-mp="axij58lj57eg">
      <ul ref={trackRef} onScroll={sync} onKeyDown={onKeyDown} tabIndex={0} aria-label="Use left and right arrow keys to browse reviews" className="relative -mx-6 flex snap-x snap-mandatory scroll-px-6 gap-4 overflow-x-auto px-6 pb-1 outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-champagne-400 md:gap-6 lg:mx-0 lg:scroll-px-0 lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-289" data-mp="azv9a91fyo2l">
        {reviews.map((review, i) => <li key={review.id} aria-roledescription="slide" aria-label={`${i + 1} of ${reviews.length}`} className="w-[86%] shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] md:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]" data-id="element-290" data-mp="1qgdjdrkbcxv8" data-mpi={i}>
            <ReviewCard review={review} index={i} data-id="element-291" data-mp="399ne81k8stge" />
          </li>)}
      </ul>

      {overflowing && <div className="mt-8 flex items-center justify-between gap-4" data-id="element-292" data-mp="3ddbncooivx3r">
          <div className="flex items-center gap-2" data-id="element-293" data-mp="3ibmxl0iqc9mi">
            {reviews.map((review, i) => <button key={review.id} type="button" onClick={() => goTo(i)} aria-label={`Show review ${i + 1}`} aria-current={active === i ? 'true' : undefined} className="flex h-6 items-center" data-id="element-294" data-mp="3n8n41cjhree5" data-mpi={i}>
                <span className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-7 bg-champagne-300' : 'w-3 bg-champagne-100/25 hover:bg-champagne-100/50'}`} data-id="element-295" data-mp="4udugp6kojky" />
              </button>)}
          </div>
          <div className="flex gap-2" data-id="element-296" data-mp="3sd72bvymzpdl">
            <button type="button" onClick={() => goTo(active - 1)} disabled={!canPrev} aria-label="Previous review" className={arrowClass} data-id="element-297" data-mp="3sc5t7x16lcyi">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-298" data-mp="aya2qd551yi1" />
            </button>
            <button type="button" onClick={() => goTo(active + 1)} disabled={!canNext} aria-label="Next review" className={arrowClass} data-id="element-299" data-mp="5zp1qbi2basj">
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-300" data-mp="39el54s68qiey" />
            </button>
          </div>
        </div>}
    </div>;
}